import React, { useState } from "react";
import { useProject } from "@/context/ProjectContext";
import { getMetrics, metricGap } from "@/lib/metrics";
import { ClipboardList, ChevronDown, Target, Users, TrendingDown, Stethoscope } from "lucide-react";

const CAUSE_FACTORS = [
  { key: "information", label: "Information", group: "environmental" },
  { key: "tools", label: "Tools & Resources", group: "environmental" },
  { key: "incentives", label: "Incentives", group: "environmental" },
  { key: "knowledge_skills", label: "Knowledge / Skills", group: "individual" },
  { key: "motives", label: "Motives", group: "individual" },
];

// Derive all Phase 1 + Phase 2 context the card displays.
function useAnalysisConstraints(phaseData) {
  const p1 = phaseData?.phase1 || {};
  const p2 = phaseData?.phase2 || {};
  const businessGoals = (p1.business_goals || "").trim();
  const persona = (p1.target_persona || "").trim();
  const metrics = getMetrics(p1);
  const desiredState = (p1.desired_state || "").trim();
  const currentState = (p1.current_state || "").trim();
  const hasGap = metrics.length > 0 || !!desiredState || !!currentState;
  const flaggedCauses = CAUSE_FACTORS.filter((f) => p2[f.group]?.[f.key]);
  const causeNotes = (p2.notes || "").trim();
  const hasCauses = flaggedCauses.length > 0 || !!causeNotes;
  const isEmpty = !businessGoals && !persona && !hasGap && !hasCauses;
  return { businessGoals, persona, metrics, desiredState, currentState, hasGap, flaggedCauses, causeNotes, hasCauses, isEmpty };
}

const TextContent = ({ value }) => <p className="text-sm text-slate-600 whitespace-pre-wrap">{value}</p>;

function GapsContent({ metrics, desiredState, currentState }) {
  return (
    <div className="space-y-1.5">
      {metrics.map((m, i) => {
        const g = metricGap(m);
        return (
          <p key={m.id || i} className="text-sm text-slate-700 font-medium">
            {m.name || "Metric"}: <span className="text-emerald-600">{g === null ? "—" : g}{m.unit ? ` ${m.unit}` : ""}</span>
          </p>
        );
      })}
      {desiredState && <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Desired:</span> {desiredState}</p>}
      {currentState && <p className="text-xs text-slate-500"><span className="font-medium text-slate-600">Current:</span> {currentState}</p>}
    </div>
  );
}

function CausesContent({ flaggedCauses, causeNotes }) {
  return (
    <div className="space-y-2">
      {flaggedCauses.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" data-testid="constraint-cause-chips">
          {flaggedCauses.map((f) => (
            <span key={f.key} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              {f.label}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic">No factors flagged in Phase 2.</p>
      )}
      {causeNotes && <p className="text-xs text-slate-400 whitespace-pre-wrap line-clamp-4">{causeNotes}</p>}
    </div>
  );
}

function ConstraintRow({ icon: Icon, label, content }) {
  return (
    <div data-testid={`constraint-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-emerald-400" />
        <span className="text-xs font-semibold text-slate-300">{label}</span>
      </div>
      {content || <p className="text-xs text-slate-500 italic">Not defined in earlier phases.</p>}
    </div>
  );
}

export default function ConstraintsContextCard() {
  const { phaseData } = useProject();
  const [open, setOpen] = useState(true);
  const c = useAnalysisConstraints(phaseData);

  const items = [
    { icon: Target, label: "Business Goals", content: c.businessGoals ? <TextContent value={c.businessGoals} /> : null },
    { icon: Users, label: "Target Audience Persona", content: c.persona ? <TextContent value={c.persona} /> : null },
    { icon: TrendingDown, label: "Core Performance Gaps", content: c.hasGap ? <GapsContent metrics={c.metrics} desiredState={c.desiredState} currentState={c.currentState} /> : null },
    { icon: Stethoscope, label: "Diagnosed Root Causes", content: c.hasCauses ? <CausesContent flaggedCauses={c.flaggedCauses} causeNotes={c.causeNotes} /> : null },
  ];

  return (
    <aside data-testid="constraints-context-card" className="lg:w-80 lg:flex-shrink-0">
      <div className="lg:sticky lg:top-40 bg-slate-900 rounded-lg overflow-hidden border border-slate-800">
        <button
          data-testid="constraints-toggle"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-white font-display font-semibold text-sm">
            <ClipboardList className="h-4 w-4 text-emerald-400" /> Analysis Constraints
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
        </button>

        {open && (
          <div className="px-4 pb-4 space-y-4 border-t border-slate-800 pt-4">
            <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">Pulled from Phase 1 &amp; 2</p>
            {c.isEmpty ? (
              <p data-testid="constraints-empty" className="text-sm text-slate-400 leading-relaxed">
                No analysis constraints defined yet.
              </p>
            ) : (
              items.map((it) => <ConstraintRow key={it.label} icon={it.icon} label={it.label} content={it.content} />)
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
